/**
 * Genera e scarica il PDF "Riepilogo Operativo Collaboratore" (con o senza costi).
 */

import jsPDF from 'jspdf';
import autoTable, { RowInput } from 'jspdf-autotable';
import { roundHoursTo10Min } from '../utils/utils';

// Ore arrotondate a blocchi di 10 minuti, come EconomicUtils.roundHoursTo10Min
// lato backend. Formato "Xh Ym", "Xh" senza minuti, "-" per zero.
const fmtOre = (h: number | null | undefined): string => {
    const r = roundHoursTo10Min(h);
    if (r <= 0) return '-';
    const totalMin = Math.round(r * 60);
    const hours = Math.floor(totalMin / 60);
    const minutes = totalMin % 60;
    return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
};

// ─────────────────────────────────────────────────────────────────────────────
// Tipi
// ─────────────────────────────────────────────────────────────────────────────

export interface SpesaDettaglio {
    voce: string;
    importo: number;
}

export interface ReportAttivitaGiornaliera {
    data: string;           // YYYY-MM-DD
    praticaNome: string;
    oreInvestigazioni: number;
    oreSicurezza: number;
    kmPercorsi: number;
    speseExtra: SpesaDettaglio[];
    orariCliente?: string;
    totaleEuroGiorno: number;
}

export interface ReportPersonaleItem {
    utenteId: number;
    nomeCompleto: string;
    ruolo: string;
    sede?: string;
    tariffaOrariaInv: number;
    tariffaOrariaSic: number;
    tariffaRimborsoKm: number;
    oreInvestigazioni: number;
    oreSicurezza: number;
    kmPercorsi: number;
    totaleEuroInvestigazioni: number;
    totaleEuroSicurezza: number;
    totaleEuroKm: number;
    totaleEuroSpeseExtra: number;
    granTotaleEuro: number;
    numeroReportApprovati: number;
    attivitaGiornaliere: ReportAttivitaGiornaliera[];
}

export interface ReportPdfOptions {
    /** Data URL (base64 PNG/JPG) del logo aziendale da disegnare in header. */
    logoDataUrl?: string | null;
    /** Ragione sociale: appare a destra in header. */
    agencyName?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Palette colori
// ─────────────────────────────────────────────────────────────────────────────
const C = {
    navy:       [15,  23,  42]  as [number,number,number],
    blue:       [37,  99,  235] as [number,number,number],
    blueLight:  [239, 246, 255] as [number,number,number],
    green:      [5,   150, 105] as [number,number,number],
    greenLight: [236, 253, 245] as [number,number,number],
    gray:       [107, 114, 128] as [number,number,number],
    grayLight:  [248, 250, 252] as [number,number,number],
    border:     [226, 232, 240] as [number,number,number],
    white:      [255, 255, 255] as [number,number,number],
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper
// ─────────────────────────────────────────────────────────────────────────────
const euro = (v: number) => `€ ${v.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    try {
        const d = new Date(dateStr);
        return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return dateStr; }
};

const mesiNomi = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

// ─────────────────────────────────────────────────────────────────────────────
// Header / footer
// Logo a sinistra e ragione sociale a destra; footer con paginazione.
// Applicati a tutte le pagine alla fine, quando il numero di pagine è noto.
// ─────────────────────────────────────────────────────────────────────────────
const HEADER_H = 22;

const drawHeaderFooter = (
    doc: jsPDF,
    meseLabel: string,
    docLabel: string,
    opts?: ReportPdfOptions,
) => {
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const now = new Date().toLocaleDateString('it-IT', { day:'2-digit', month:'2-digit', year:'numeric' });
    const pages = doc.getNumberOfPages();
    const agency = opts?.agencyName?.trim() || 'IURIS Investigazioni e Sicurezza';

    for (let i = 1; i <= pages; i++) {
        doc.setPage(i);

        // Header
        doc.setFillColor(...C.navy);
        doc.rect(0, 0, W, HEADER_H, 'F');
        doc.setFillColor(...C.blue);
        doc.rect(0, 0, 3, HEADER_H, 'F');

        // Logo in un box fisso (14mm di altezza, max 30mm di larghezza): jsPDF non
        // conosce le dimensioni reali del data URL.
        if (opts?.logoDataUrl) {
            try {
                doc.addImage(opts.logoDataUrl, 'PNG', 8, 4, 30, HEADER_H - 8, undefined, 'FAST');
            } catch {
                // se l'immagine non è renderizzabile non blocchiamo il PDF
            }
        }

        // Ragione sociale + label documento a destra
        doc.setTextColor(...C.white);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text(agency.toUpperCase(), W - 8, 9, { align: 'right' });
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text(`${docLabel}  ·  ${meseLabel}`, W - 8, 14.5, { align: 'right' });

        // Footer
        doc.setFillColor(...C.grayLight);
        doc.rect(0, H - 10, W, 10, 'F');
        doc.setDrawColor(...C.border);
        doc.setLineWidth(0.3);
        doc.line(0, H - 10, W, H - 10);

        doc.setTextColor(...C.gray);
        doc.setFontSize(7);
        doc.text(`Generato il ${now}  —  Documento interno riservato`, 8, H - 3.5);
        doc.text(`Pagina ${i} / ${pages}`, W - 8, H - 3.5, { align: 'right' });
    }
};

// ─────────────────────────────────────────────────────────────────────────────
// Sezione per collaboratore
// Titolo, riga info (periodo, tariffe e totale se con costi), timesheet giornaliero
// e sintesi mensile. Usata sia per il PDF singolo sia per quello completo.
// Restituisce il cursorY a fine sezione.
// ─────────────────────────────────────────────────────────────────────────────
const drawCollaboratoreSection = (
    doc: jsPDF,
    p: ReportPersonaleItem,
    meseLabel: string,
    cursorY: number,
    opts: { withCosts: boolean }
): number => {
    const W = doc.internal.pageSize.getWidth();

    // ── Salto pagina all'inizio della sezione se serve
    if (cursorY > 240) { doc.addPage(); cursorY = HEADER_H + 8; }

    // ── Titolo sezione
    doc.setTextColor(...C.navy);
    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.text(`Riepilogo Operativo ${p.nomeCompleto}`, 12, cursorY);
    cursorY += 5;

    // ── Riga info: tariffe e totale solo nella versione con costi
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.gray);
    const infoParts: string[] = [`Periodo: ${meseLabel}`];
    if (opts.withCosts) {
        infoParts.push(`Tariffa Inv: ${euro(p.tariffaOrariaInv)}/h`);
        infoParts.push(`Sicurezza: ${euro(p.tariffaOrariaSic)}/h`);
        infoParts.push(`Km: ${euro(p.tariffaRimborsoKm)}`);
        infoParts.push(`Totale: ${euro(p.granTotaleEuro)}`);
    } else {
        infoParts.push(`Ore Inv: ${fmtOre(p.oreInvestigazioni)}`);
        infoParts.push(`Sicurezza: ${fmtOre(p.oreSicurezza)}`);
        infoParts.push(`Km: ${p.kmPercorsi}`);
    }
    doc.text(infoParts.join('  ·  '), 12, cursorY);
    cursorY += 3;

    doc.setDrawColor(...C.blue);
    doc.setLineWidth(0.6);
    doc.line(12, cursorY, W - 12, cursorY);
    cursorY += 6;

    // ── Timesheet giornaliero
    if (p.attivitaGiornaliere && p.attivitaGiornaliere.length > 0) {
        const attivitaOrdinate = [...p.attivitaGiornaliere].sort(
            (a, b) => new Date(a.data).getTime() - new Date(b.data).getTime()
        );

        // Una riga principale per giorno (data, pratica, ore, km, spese, totale) e,
        // se ci sono scontrini, una sotto-riga a tutta larghezza con il dettaglio,
        // così il report giornaliero resta unito.
        const formatSpeseRiga = (g: ReportAttivitaGiornaliera): string => {
            if (!g.speseExtra.length) return '';
            return '  Spese:  ' + g.speseExtra.map(s => `${s.voce} (€ ${s.importo.toFixed(2)})`).join('   ·   ');
        };

        if (opts.withCosts) {
            const head: RowInput[] = [[
                { content: 'Data',              styles: { halign: 'center' } },
                { content: 'Mandato / Servizio', styles: { halign: 'left'  } },
                { content: 'Ore Inv.',          styles: { halign: 'center' } },
                { content: 'Ore Ser.',          styles: { halign: 'center' } },
                { content: 'Km',                styles: { halign: 'center' } },
                { content: 'Spese €',           styles: { halign: 'right' } },
                { content: 'Tot. €',            styles: { halign: 'right' } },
            ]];
            const body: RowInput[] = [];
            attivitaOrdinate.forEach(g => {
                const totSpeseG = g.speseExtra.reduce((s, sp) => s + sp.importo, 0);
                // Una sola riga per giorno, con le spese come seconda riga nella cella Mandato:
                // con rowPageBreak:'avoid' il giorno non viene mai spezzato tra due pagine.
                const speseTxt = formatSpeseRiga(g).trim();
                const mandatoCell = speseTxt ? `${g.praticaNome}\n${speseTxt}` : g.praticaNome;
                body.push([
                    { content: formatDate(g.data), styles: { halign: 'center', fontStyle: 'bold', textColor: C.blue } },
                    { content: mandatoCell,        styles: { halign: 'left' } },
                    { content: fmtOre(g.oreInvestigazioni), styles: { halign: 'center' } },
                    { content: fmtOre(g.oreSicurezza),      styles: { halign: 'center' } },
                    { content: g.kmPercorsi > 0 ? `${g.kmPercorsi}` : '-', styles: { halign: 'center' } },
                    { content: totSpeseG > 0 ? euro(totSpeseG) : '-',     styles: { halign: 'right' } },
                    { content: euro(g.totaleEuroGiorno), styles: { halign: 'right', fontStyle: 'bold', textColor: C.navy } },
                ]);
            });

            autoTable(doc, {
                startY: cursorY,
                rowPageBreak: 'avoid',
                head, body,
                margin: { top: HEADER_H + 4, left: 12, right: 12, bottom: 14 },
                headStyles: { fillColor: C.blueLight, textColor: C.blue, fontStyle: 'bold', fontSize: 8 },
                bodyStyles: { fontSize: 8, cellPadding: 2 },
                columnStyles: {
                    0: { cellWidth: 24 },
                    1: { cellWidth: 'auto' },
                    2: { cellWidth: 18 },
                    3: { cellWidth: 18 },
                    4: { cellWidth: 14 },
                    5: { cellWidth: 24 },
                    6: { cellWidth: 24 },
                },
                tableLineWidth: 0.2,
                tableLineColor: C.border,
            });
        } else {
            const head: RowInput[] = [[
                { content: 'Data',              styles: { halign: 'center' } },
                { content: 'Mandato / Servizio', styles: { halign: 'left'  } },
                { content: 'Ore Inv.',          styles: { halign: 'center' } },
                { content: 'Ore Ser.',          styles: { halign: 'center' } },
                { content: 'Km',                styles: { halign: 'center' } },
                { content: 'Spese €',           styles: { halign: 'right' } },
            ]];
            const body: RowInput[] = [];
            attivitaOrdinate.forEach(g => {
                const totSpeseG = g.speseExtra.reduce((s, sp) => s + sp.importo, 0);
                // Una sola riga per giorno: con rowPageBreak:'avoid' non si spezza tra due pagine.
                const speseTxt = formatSpeseRiga(g).trim();
                const mandatoCell = speseTxt ? `${g.praticaNome}\n${speseTxt}` : g.praticaNome;
                body.push([
                    { content: formatDate(g.data), styles: { halign: 'center', fontStyle: 'bold', textColor: C.blue } },
                    { content: mandatoCell,        styles: { halign: 'left' } },
                    { content: fmtOre(g.oreInvestigazioni), styles: { halign: 'center' } },
                    { content: fmtOre(g.oreSicurezza),      styles: { halign: 'center' } },
                    { content: g.kmPercorsi > 0 ? `${g.kmPercorsi}` : '-', styles: { halign: 'center' } },
                    { content: totSpeseG > 0 ? `€ ${totSpeseG.toFixed(2)}` : '-', styles: { halign: 'right' } },
                ]);
            });

            autoTable(doc, {
                startY: cursorY,
                rowPageBreak: 'avoid',
                head, body,
                margin: { top: HEADER_H + 4, left: 12, right: 12, bottom: 14 },
                headStyles: { fillColor: C.blueLight, textColor: C.blue, fontStyle: 'bold', fontSize: 8 },
                bodyStyles: { fontSize: 8, cellPadding: 2 },
                columnStyles: {
                    0: { cellWidth: 24 },
                    1: { cellWidth: 'auto' },
                    2: { cellWidth: 18 },
                    3: { cellWidth: 18 },
                    4: { cellWidth: 14 },
                    5: { cellWidth: 28 },
                },
                tableLineWidth: 0.2,
                tableLineColor: C.border,
            });
        }

        cursorY = (doc as any).lastAutoTable.finalY + 8;
    } else {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(9);
        doc.setTextColor(...C.gray);
        doc.text('Nessuna attività registrata per questo collaboratore nel mese corrente.', 12, cursorY);
        cursorY += 8;
    }

    // ── Sintesi mensile finale
    if (cursorY > 240) { doc.addPage(); cursorY = HEADER_H + 8; }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...C.navy);
    doc.text(`Sintesi mensile · ${p.nomeCompleto}`, 12, cursorY);
    cursorY += 3;

    if (opts.withCosts) {
        const costoBody: RowInput[] = [
            ['Totale Ore Investigazioni', euro(p.tariffaOrariaInv), fmtOre(p.oreInvestigazioni), euro(p.totaleEuroInvestigazioni)],
            ['Totale Ore Sicurezza',      euro(p.tariffaOrariaSic), fmtOre(p.oreSicurezza),      euro(p.totaleEuroSicurezza)],
            ['Totale Rimborso Chilometrico', euro(p.tariffaRimborsoKm), `${p.kmPercorsi} km`, euro(p.totaleEuroKm)],
            ['Totale Spese Extra (rimborsi piè di lista)', '—', '—', euro(p.totaleEuroSpeseExtra)],
            [
                { content: 'TOTALE COMPENSO SPETTANTE', colSpan: 3, styles: { halign: 'right', fontStyle: 'bold', textColor: C.navy } },
                { content: euro(p.granTotaleEuro), styles: { halign: 'right', fontStyle: 'bold', textColor: C.green, fontSize: 11 } },
            ],
        ];

        autoTable(doc, {
            startY: cursorY,
            head: [['Voce di Costo', 'Tariffa Base', 'Quantità Mensile', 'Importo Totale']],
            body: costoBody,
            margin: { top: HEADER_H + 4, left: 12, right: 12, bottom: 14 },
            headStyles: { fillColor: C.navy, textColor: C.white, fontStyle: 'bold', fontSize: 8 },
            bodyStyles: { fontSize: 8.5 },
            columnStyles: {
                0: { cellWidth: 90, halign: 'left' },
                1: { cellWidth: 30, halign: 'center' },
                2: { cellWidth: 30, halign: 'center' },
                3: { cellWidth: 36, halign: 'right' },
            },
            didParseCell: (hookData) => {
                if (hookData.section === 'body' && hookData.row.index === costoBody.length - 1) {
                    hookData.cell.styles.fillColor = C.greenLight;
                    hookData.cell.styles.lineWidth = { top: 0.5 };
                    hookData.cell.styles.lineColor = C.green;
                }
            },
            tableLineWidth: 0.3,
            tableLineColor: C.border,
        });
    } else {
        const totSpese = p.attivitaGiornaliere.reduce(
            (s, g) => s + g.speseExtra.reduce((ss, sp) => ss + sp.importo, 0), 0
        );
        autoTable(doc, {
            startY: cursorY,
            head: [['Voce', 'Quantità Mensile']],
            body: [
                ['Ore Investigazioni', fmtOre(p.oreInvestigazioni)],
                ['Ore Sicurezza',      fmtOre(p.oreSicurezza)],
                ['Km Percorsi',        `${p.kmPercorsi} km`],
                ['Spese Rimborsabili (pasti, pedaggi…)', `€ ${totSpese.toFixed(2)}`],
            ],
            margin: { top: HEADER_H + 4, left: 12, right: 12, bottom: 14 },
            headStyles: { fillColor: C.navy, textColor: C.white, fontStyle: 'bold', fontSize: 8 },
            bodyStyles: { fontSize: 8.5 },
            columnStyles: {
                0: { cellWidth: 120, halign: 'left' },
                1: { cellWidth: 66,  halign: 'center', fontStyle: 'bold' },
            },
            tableLineWidth: 0.3,
            tableLineColor: C.border,
        });
    }

    return (doc as any).lastAutoTable.finalY + 12;
};

// ─────────────────────────────────────────────────────────────────────────────
// Riepilogo complessivo (solo multi-collaboratore)
// Tabella iniziale con tutti i collaboratori e totale aziendale.
// ─────────────────────────────────────────────────────────────────────────────
const drawTotaleAziendale = (
    doc: jsPDF,
    data: ReportPersonaleItem[],
    meseLabel: string,
    cursorY: number,
    opts: { withCosts: boolean }
): number => {
    const W = doc.internal.pageSize.getWidth();
    const grandTotal = data.reduce((s, p) => s + p.granTotaleEuro, 0);
    // Arrotondamento a 10 minuti per collaboratore prima della somma, come fa il backend.
    const totOreInv = data.reduce((s, p) => s + roundHoursTo10Min(p.oreInvestigazioni), 0);
    const totOreSic = data.reduce((s, p) => s + roundHoursTo10Min(p.oreSicurezza), 0);
    const totKm     = data.reduce((s, p) => s + p.kmPercorsi, 0);

    doc.setTextColor(...C.navy);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Riepilogo Operativo Personale', 12, cursorY);
    cursorY += 5;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.gray);
    const summaryLine = opts.withCosts
        ? `Periodo: ${meseLabel}  ·  Collaboratori: ${data.length}  ·  Totale complessivo: ${euro(grandTotal)}`
        : `Periodo: ${meseLabel}  ·  Collaboratori: ${data.length}`;
    doc.text(summaryLine, 12, cursorY);
    cursorY += 3;

    doc.setDrawColor(...C.blue);
    doc.setLineWidth(0.8);
    doc.line(12, cursorY, W - 12, cursorY);
    cursorY += 6;

    // Tabella sintetica
    const head: RowInput[] = [[
        { content: 'Collaboratore', styles: { halign: 'left'  } },
        { content: 'Ore Inv.',      styles: { halign: 'center'} },
        { content: 'Ore Sic.',      styles: { halign: 'center'} },
        { content: 'Km Tot.',       styles: { halign: 'center'} },
        ...(opts.withCosts ? [{ content: 'Costo Netto €', styles: { halign: 'right' as const } }] : []),
    ]];

    const body: RowInput[] = data.map(p => {
        const row: any[] = [
            { content: p.nomeCompleto, styles: { fontStyle: 'bold' } },
            { content: fmtOre(p.oreInvestigazioni), styles: { halign: 'center' } },
            { content: fmtOre(p.oreSicurezza),      styles: { halign: 'center' } },
            { content: `${p.kmPercorsi}`,            styles: { halign: 'center' } },
        ];
        if (opts.withCosts) {
            row.push({ content: euro(p.granTotaleEuro), styles: { halign: 'right', fontStyle: 'bold', textColor: C.navy } });
        }
        return row;
    });

    body.push([
        { content: 'TOTALE COMPLESSIVO AZIENDA', styles: { fontStyle: 'bold', textColor: C.navy } },
        { content: fmtOre(totOreInv), styles: { halign: 'center', fontStyle: 'bold' } },
        { content: fmtOre(totOreSic), styles: { halign: 'center', fontStyle: 'bold' } },
        { content: `${totKm}`,        styles: { halign: 'center', fontStyle: 'bold' } },
        ...(opts.withCosts ? [{ content: euro(grandTotal), styles: { halign: 'right' as const, fontStyle: 'bold' as const, textColor: C.green } }] : []),
    ] as any);

    autoTable(doc, {
        startY: cursorY,
        head, body,
        margin: { top: HEADER_H + 4, left: 12, right: 12, bottom: 14 },
        headStyles: { fillColor: C.navy, textColor: C.white, fontStyle: 'bold', fontSize: 8 },
        bodyStyles: { fontSize: 8.5 },
        alternateRowStyles: { fillColor: C.grayLight },
        didParseCell: (hookData) => {
            if (hookData.section === 'body' && hookData.row.index === body.length - 1) {
                hookData.cell.styles.fillColor = opts.withCosts ? C.greenLight : C.blueLight;
                hookData.cell.styles.lineWidth = { top: 0.5 };
                hookData.cell.styles.lineColor = opts.withCosts ? C.green : C.blue;
            }
        },
        tableLineWidth: 0.3,
        tableLineColor: C.border,
    });

    return (doc as any).lastAutoTable.finalY + 14;
};

// ─────────────────────────────────────────────────────────────────────────────
// Builder interno, con costi
// ─────────────────────────────────────────────────────────────────────────────
const buildDoc = (data: ReportPersonaleItem[], anno: number, mese: number, opts?: ReportPdfOptions): jsPDF => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const meseLabel = `${mesiNomi[mese - 1]} ${anno}`;

    let cursorY = HEADER_H + 8;

    if (data.length === 1) {
        // Singolo: solo la sezione del collaboratore
        drawCollaboratoreSection(doc, data[0], meseLabel, cursorY, { withCosts: true });
    } else {
        // Multi: riepilogo aziendale e poi una sezione per collaboratore
        cursorY = drawTotaleAziendale(doc, data, meseLabel, cursorY, { withCosts: true });
        for (const p of data) {
            cursorY = drawCollaboratoreSection(doc, p, meseLabel, cursorY, { withCosts: true });
        }
    }

    drawHeaderFooter(doc, meseLabel, 'Riepilogo Operativo', opts);
    return doc;
};

// ─────────────────────────────────────────────────────────────────────────────
// Builder interno, senza costi
// ─────────────────────────────────────────────────────────────────────────────
const buildDocSenzaCosti = (data: ReportPersonaleItem[], anno: number, mese: number, opts?: ReportPdfOptions): jsPDF => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const meseLabel = `${mesiNomi[mese - 1]} ${anno}`;

    let cursorY = HEADER_H + 8;

    if (data.length === 1) {
        drawCollaboratoreSection(doc, data[0], meseLabel, cursorY, { withCosts: false });
    } else {
        cursorY = drawTotaleAziendale(doc, data, meseLabel, cursorY, { withCosts: false });
        for (const p of data) {
            cursorY = drawCollaboratoreSection(doc, p, meseLabel, cursorY, { withCosts: false });
        }
    }

    drawHeaderFooter(doc, meseLabel, 'Riepilogo Operativo · Nessun valore economico di compenso', opts);
    return doc;
};

// ─────────────────────────────────────────────────────────────────────────────
// Export pubblici
// ─────────────────────────────────────────────────────────────────────────────

export const generateReportPdfBlob = (data: ReportPersonaleItem[], anno: number, mese: number, opts?: ReportPdfOptions): Blob => {
    return buildDoc(data, anno, mese, opts).output('blob');
};

export const generateReportPdf = (data: ReportPersonaleItem[], anno: number, mese: number, opts?: ReportPdfOptions): void => {
    const doc = buildDoc(data, anno, mese, opts);
    const fileName = data.length === 1
        ? `report_${data[0].nomeCompleto.replace(/\s+/g, '_').toLowerCase()}_${mesiNomi[mese-1].toLowerCase()}_${anno}.pdf`
        : `report_personale_${mesiNomi[mese-1].toLowerCase()}_${anno}.pdf`;
    doc.save(fileName);
};

export const generateReportSenzaCostiBlob = (data: ReportPersonaleItem[], anno: number, mese: number, opts?: ReportPdfOptions): Blob => {
    return buildDocSenzaCosti(data, anno, mese, opts).output('blob');
};

export const generateReportSenzaCosti = (data: ReportPersonaleItem[], anno: number, mese: number, opts?: ReportPdfOptions): void => {
    const doc = buildDocSenzaCosti(data, anno, mese, opts);
    const fileName = data.length === 1
        ? `riepilogo_${data[0].nomeCompleto.replace(/\s+/g, '_').toLowerCase()}_${mesiNomi[mese-1].toLowerCase()}_${anno}.pdf`
        : `riepilogo_operativo_${mesiNomi[mese-1].toLowerCase()}_${anno}.pdf`;
    doc.save(fileName);
};
